import { createSelector } from '@reduxjs/toolkit'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import type { IProgramType } from 'legacy/lut/features/partner360/configuration'
import type { IState } from 'legacy/lut/store'

import {
  decodeAttentionStatusMask,
  getAttentionMasksForOffering,
  parseAttentionStatusMasks,
} from 'partner360/entities/attentionStatuses'

import type { ICockpitPartnerData } from '../types'

import { selectCockpitAttentionStatusCatalog } from './selectCockpitAttentionStatusCatalog'
import { selectCockpitFirstPartnerOfferingsCatalog } from './selectCockpitFirstPartnerOfferingsCatalog'
import { selectCockpitOffering } from './selectCockpitOffering'
import { selectCockpitPartnerData } from './selectCockpitPartnerData'
import { selectVisibleCockpitPartnerData } from './selectVisibleCockpitPartnerData'

/**
 * The partner's masks without the offerings it has switched off: a switched-off offering is not
 * planned, so it does not count towards the partner as a whole until it is switched back on.
 * Without a catalog every mask counts.
 */
const getPlannedOfferingMasks = (
  partner: ICockpitPartnerData | undefined,
  offeringsCatalog?: IProgramType[]
) => {
  const masks = parseAttentionStatusMasks(partner?.CockpitAttentionStatus)

  if (!offeringsCatalog?.length) {
    return masks
  }

  const enabledOfferings = new Set<string>(
    offeringsCatalog.filter(({ enabled }) => enabled).map(({ programType }) => programType)
  )

  return masks.filter(({ offering }) => enabledOfferings.has(offering))
}

/**
 * Attention statuses the cockpit's partner has raised, one row per offering that raised them.
 *
 * The server sends the catalog of statuses that can exist and, on the partner's own row, the
 * `CockpitAttentionStatus` masks that say which of them it raised per offering; this decodes the
 * one against the other. An offering the partner carries no mask for raises nothing. `Overall` is
 * no offering to plan for: it reads every offering's mask and keeps the offerings apart, so a
 * status two offerings raise is two rows, each titled for and answered by its own offering.
 */
const decodePartnerAttentionStatuses = (
  catalog: ReturnType<typeof selectCockpitAttentionStatusCatalog>,
  partner: ICockpitPartnerData | undefined,
  selectedOffering: ProgramTypes,
  offeringsCatalog?: IProgramType[]
) => {
  const offering = selectedOffering === ProgramTypes.Overall ? undefined : selectedOffering

  /* A switched-off offering still lists its statuses on its own tab; `Overall` sums up the
     planned offerings only. */
  const masks = getAttentionMasksForOffering({
    masks:
      offering === undefined
        ? getPlannedOfferingMasks(partner, offeringsCatalog)
        : parseAttentionStatusMasks(partner?.CockpitAttentionStatus),
    offering,
  })

  return masks
    .flatMap(({ offering: maskOffering, mask }) =>
      decodeAttentionStatusMask({ mask, catalog, offering: maskOffering })
    )
    .sort((left, right) => left.sortOrder - right.sortOrder)
}

export const selectCockpitAttentionStatuses = createSelector(
  [
    selectCockpitAttentionStatusCatalog,
    selectCockpitPartnerData,
    selectCockpitOffering,
    selectCockpitFirstPartnerOfferingsCatalog,
  ],
  (catalog, partnerData, selectedOffering, offeringsCatalog) =>
    decodePartnerAttentionStatuses(catalog, partnerData[0], selectedOffering, offeringsCatalog)
)

/**
 * The cockpit's partner's rows across every planned offering, whatever offering is in view: what
 * an opened status reads to name the other offerings that raised it too.
 */
export const selectCockpitAllOfferingsAttentionStatuses = createSelector(
  [
    selectCockpitAttentionStatusCatalog,
    selectCockpitPartnerData,
    selectCockpitFirstPartnerOfferingsCatalog,
  ],
  (catalog, partnerData, offeringsCatalog) =>
    decodePartnerAttentionStatuses(catalog, partnerData[0], ProgramTypes.Overall, offeringsCatalog)
)

/**
 * The same rows for one partner of the manager cockpit's payload, picked by id: what that partner
 * would list on its own cockpit, one row per offering that raised a status.
 */
export const selectCockpitPartnerAttentionStatuses = createSelector(
  [
    selectCockpitAttentionStatusCatalog,
    selectCockpitPartnerData,
    selectCockpitOffering,
    (_state: IState, partnerId: string) => partnerId,
  ],
  (catalog, partnerData, selectedOffering, partnerId) =>
    decodePartnerAttentionStatuses(
      catalog,
      partnerData.find(({ PartnerId }) => PartnerId === partnerId),
      selectedOffering
    )
)

/**
 * The most severe level the cockpit's partner raised per offering, whatever offering is in view:
 * what the programme tabs are coloured by. An offering that raised nothing has no entry.
 */
const getAttentionSeverityByOffering = (
  catalog: ReturnType<typeof selectCockpitAttentionStatusCatalog>,
  partners: (ICockpitPartnerData | undefined)[],
  offeringsCatalog?: IProgramType[]
) => {
  const severities = new Map<string, number>()

  partners.forEach((partner) =>
    getPlannedOfferingMasks(partner, offeringsCatalog).forEach(({ offering, mask }) => {
      decodeAttentionStatusMask({ mask, catalog, offering }).forEach(({ severity }) => {
        severities.set(offering, Math.max(severities.get(offering) ?? severity, severity))
      })
    })
  )

  return severities
}

export const selectCockpitAttentionSeverityByOffering = createSelector(
  [
    selectCockpitAttentionStatusCatalog,
    selectCockpitPartnerData,
    selectCockpitFirstPartnerOfferingsCatalog,
  ],
  (catalog, partnerData, offeringsCatalog) =>
    getAttentionSeverityByOffering(catalog, [partnerData[0]], offeringsCatalog)
)

/**
 * The same levels across every partner the manager cockpit shows: per offering, the most severe
 * any of them raised.
 */
export const selectVisibleCockpitAttentionSeverityByOffering = createSelector(
  [selectCockpitAttentionStatusCatalog, selectVisibleCockpitPartnerData],
  (catalog, partnerData) => getAttentionSeverityByOffering(catalog, partnerData)
)

export interface ICockpitAttentionCounts {
  overall: number
  /** An offering that raised nothing has no entry. */
  byOffering: ReadonlyMap<string, number>
}

/**
 * How many statuses the cockpit's partner raised per planned offering — the rows its tab lists —
 * and on `Overall`, which lists every planned offering's rows apart, their sum.
 */
export const selectCockpitAttentionCountByOffering = createSelector(
  [
    selectCockpitAttentionStatusCatalog,
    selectCockpitPartnerData,
    selectCockpitFirstPartnerOfferingsCatalog,
  ],
  (catalog, partnerData, offeringsCatalog): ICockpitAttentionCounts => {
    const byOffering = new Map<string, number>()

    getPlannedOfferingMasks(partnerData[0], offeringsCatalog).forEach(({ offering, mask }) => {
      const count = decodeAttentionStatusMask({ mask, catalog, offering }).length

      if (count) {
        byOffering.set(offering, (byOffering.get(offering) ?? 0) + count)
      }
    })

    return {
      overall: [...byOffering.values()].reduce((sum, count) => sum + count, 0),
      byOffering,
    }
  }
)

/**
 * The same counts across every partner the manager cockpit shows. Its tile lists a status once
 * however many partners raised it, so an offering counts its distinct statuses, and `Overall` the
 * distinct statuses of all of them.
 */
export const selectVisibleCockpitAttentionCountByOffering = createSelector(
  [selectCockpitAttentionStatusCatalog, selectVisibleCockpitPartnerData],
  (catalog, partnerData): ICockpitAttentionCounts => {
    const bitsByOffering = new Map<string, Set<number>>()
    const overallBits = new Set<number>()

    partnerData.forEach((partner) =>
      parseAttentionStatusMasks(partner.CockpitAttentionStatus).forEach(({ offering, mask }) =>
        decodeAttentionStatusMask({ mask, catalog, offering }).forEach(({ bitValue }) => {
          const bits = bitsByOffering.get(offering) ?? new Set<number>()

          bits.add(bitValue)
          bitsByOffering.set(offering, bits)
          overallBits.add(bitValue)
        })
      )
    )

    return {
      overall: overallBits.size,
      byOffering: new Map([...bitsByOffering].map(([offering, bits]) => [offering, bits.size])),
    }
  }
)
